let readObserver = null;
const pendingReadEntryIDs = new Set();
const optimisticReadEntryIDs = new Set();
let pendingReadFlushTimer = null;
const pendingUnreadRefreshCardsByID = new Map();

const dashboardReadModeStorageKey = "dashboard-read-mode";
let isPastHeaderReadTrackingAttached = false;
let isPastHeaderReadTicking = false;

function normalizeDashboardReadMode(rawMode) {
  const mode = String(rawMode || "").trim().toLowerCase();
  if (mode === "past-header" || mode === "manual") {
    return mode;
  }
  return "visible";
}

function readDashboardReadModeFromStorage() {
  try {
    return normalizeDashboardReadMode(window.localStorage.getItem(dashboardReadModeStorageKey));
  } catch (_err) {
    return "visible";
  }
}

function writeDashboardReadModeToStorage(mode) {
  try {
    window.localStorage.setItem(dashboardReadModeStorageKey, normalizeDashboardReadMode(mode));
  } catch (_err) {
    // Ignore storage failures.
  }
}

function selectedDashboardReadMode() {
  const modeSelect = document.getElementById("dashboard-read-mode");
  if (modeSelect instanceof HTMLSelectElement) {
    return normalizeDashboardReadMode(modeSelect.value);
  }
  return readDashboardReadModeFromStorage();
}

export function syncDashboardReadModeControl() {
  const modeSelect = document.getElementById("dashboard-read-mode");
  if (!(modeSelect instanceof HTMLSelectElement)) {
    return;
  }

  const desiredMode = readDashboardReadModeFromStorage();
  if (modeSelect.value !== desiredMode) {
    modeSelect.value = desiredMode;
  }

  if (modeSelect.dataset.readModeBound === "1") {
    return;
  }

  modeSelect.dataset.readModeBound = "1";
  modeSelect.addEventListener("change", function () {
    const nextMode = normalizeDashboardReadMode(modeSelect.value);
    writeDashboardReadModeToStorage(nextMode);
    initEntryReadObserver();
  });
}

export function parseDashboardURL(path) {
  try {
    return new URL(path, window.location.origin);
  } catch (_err) {
    return null;
  }
}

export function normalizedQueryValue(urlObj, key) {
  if (!urlObj) {
    return "";
  }
  return String(urlObj.searchParams.get(key) || "").trim();
}

export function isUnreadOnlyView() {
  const pathname = String(window.location.pathname || "").trim();
  if (!pathname) {
    return false;
  }
  return !pathname.endsWith("/all") && pathname !== "/all";
}

export function isStarredOnlyView() {
  const currentURL = parseDashboardURL(window.location.pathname + window.location.search);
  return normalizedQueryValue(currentURL, "starred") === "1";
}

export function extractEntryIDFromStarForm(starForm) {
  if (!(starForm instanceof HTMLFormElement)) {
    return 0;
  }
  const action = String(starForm.getAttribute("action") || "");
  const match = action.match(/^\/entries\/(\d+)\/star$/);
  if (!match || !match[1]) {
    return 0;
  }
  const entryID = Number(match[1]);
  if (!Number.isFinite(entryID) || entryID <= 0) {
    return 0;
  }
  return entryID;
}

function csrfTokenForRequests() {
  const tokenInput = document.querySelector('input[name="_csrf"]');
  if (!(tokenInput instanceof HTMLInputElement)) {
    return "";
  }
  return String(tokenInput.value || "").trim();
}

function markEntryCardReadLocally(entryID) {
  const card = document.querySelector("article[data-entry-id='" + String(entryID) + "']");
  if (!(card instanceof HTMLElement)) {
    return false;
  }
  const wasUnread = card.classList.contains("entry-state-unread");
  card.classList.remove("entry-state-unread");
  card.classList.add("entry-state-read");
  return wasUnread;
}

function markEntryCardUnreadLocally(entryID) {
  const card = document.querySelector("article[data-entry-id='" + String(entryID) + "']");
  if (!(card instanceof HTMLElement)) {
    return false;
  }
  const wasRead = card.classList.contains("entry-state-read");
  card.classList.remove("entry-state-read");
  card.classList.add("entry-state-unread");
  return wasRead;
}

function updateUnreadArticlesCounter(delta) {
  if (!Number.isFinite(delta) || delta === 0) {
    return;
  }
  const countEl = document.getElementById("loaded-entry-count");
  if (!(countEl instanceof HTMLElement)) {
    return;
  }
  const current = parseCounterText(countEl.textContent || "0");
  if (!Number.isFinite(current)) {
    return;
  }
  const next = Math.max(0, current + delta);
  countEl.textContent = formatCounterValue(next);
}

function parseCounterText(raw) {
  const normalized = String(raw || "").replace(/[^0-9-]/g, "").trim();
  if (!normalized) {
    return 0;
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return parsed;
}

function formatCounterValue(value) {
  if (!Number.isFinite(value)) {
    return "0";
  }
  return Math.trunc(value).toLocaleString("en-US");
}

function flushPendingReadEntries() {
  pendingReadFlushTimer = null;
  if (pendingReadEntryIDs.size === 0) {
    return;
  }

  const csrfToken = csrfTokenForRequests();
  if (!csrfToken) {
    return;
  }

  const ids = Array.from(pendingReadEntryIDs);
  pendingReadEntryIDs.clear();

  fetch("/entries/read", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken
    },
    body: JSON.stringify({ entry_ids: ids })
  }).then(function (response) {
    if (!response || !response.ok) {
      throw new Error("mark read failed");
    }
    ids.forEach(function (id) {
      optimisticReadEntryIDs.delete(id);
    });
  }).catch(function () {
    ids.forEach(function (id) {
      pendingReadEntryIDs.add(id);
      if (optimisticReadEntryIDs.has(id)) {
        optimisticReadEntryIDs.delete(id);
        if (markEntryCardUnreadLocally(id)) {
          updateUnreadArticlesCounter(1);
        }
      }
    });

    if (pendingReadFlushTimer === null) {
      pendingReadFlushTimer = window.setTimeout(flushPendingReadEntries, 1500);
    }
  });
}

function queueEntryAsRead(entryID) {
  if (!Number.isFinite(entryID) || entryID <= 0) {
    return;
  }
  if (pendingReadEntryIDs.has(entryID)) {
    return;
  }

  pendingReadEntryIDs.add(entryID);

  if (markEntryCardReadLocally(entryID)) {
    optimisticReadEntryIDs.add(entryID);
    updateUnreadArticlesCounter(-1);
  }

  if (pendingReadFlushTimer !== null) {
    return;
  }
  pendingReadFlushTimer = window.setTimeout(flushPendingReadEntries, 450);
}

function detachPastHeaderReadTracking() {
  if (!isPastHeaderReadTrackingAttached) {
    return;
  }
  window.removeEventListener("scroll", schedulePastHeaderReadCheck);
  window.removeEventListener("resize", schedulePastHeaderReadCheck);
  isPastHeaderReadTrackingAttached = false;
  isPastHeaderReadTicking = false;
}

function dashboardSubheaderLineY() {
  const statsBar = document.querySelector(".dashboard-stats-bar");
  if (!(statsBar instanceof HTMLElement)) {
    return 0;
  }
  return statsBar.getBoundingClientRect().bottom;
}

function queueEntriesPastSubheaderAsRead() {
  const entriesContainer = document.getElementById("entries-content");
  if (!(entriesContainer instanceof HTMLElement)) {
    return;
  }

  const thresholdY = dashboardSubheaderLineY();
  const cards = entriesContainer.querySelectorAll("article.entry-state-unread[data-entry-id]");
  cards.forEach(function (card) {
    if (!(card instanceof HTMLElement)) {
      return;
    }
    if (card.getBoundingClientRect().top > thresholdY) {
      return;
    }
    const entryID = Number(card.getAttribute("data-entry-id") || "0");
    if (entryID > 0) {
      queueEntryAsRead(entryID);
    }
  });
}

function schedulePastHeaderReadCheck() {
  if (isPastHeaderReadTicking) {
    return;
  }
  isPastHeaderReadTicking = true;
  window.requestAnimationFrame(function () {
    isPastHeaderReadTicking = false;
    queueEntriesPastSubheaderAsRead();
  });
}

function attachPastHeaderReadTracking() {
  if (isPastHeaderReadTrackingAttached) {
    return;
  }
  window.addEventListener("scroll", schedulePastHeaderReadCheck, { passive: true });
  window.addEventListener("resize", schedulePastHeaderReadCheck);
  isPastHeaderReadTrackingAttached = true;
}

export function initEntryReadObserver() {
  const entriesContainer = document.getElementById("entries-content");
  if (!(entriesContainer instanceof HTMLElement)) {
    if (readObserver) {
      readObserver.disconnect();
      readObserver = null;
    }
    detachPastHeaderReadTracking();
    return;
  }

  if (!csrfTokenForRequests()) {
    if (readObserver) {
      readObserver.disconnect();
      readObserver = null;
    }
    detachPastHeaderReadTracking();
    return;
  }

  const readMode = selectedDashboardReadMode();
  if (readMode === "manual") {
    if (readObserver) {
      readObserver.disconnect();
      readObserver = null;
    }
    detachPastHeaderReadTracking();
    return;
  }

  if (readMode === "past-header") {
    if (readObserver) {
      readObserver.disconnect();
      readObserver = null;
    }
    attachPastHeaderReadTracking();
    queueEntriesPastSubheaderAsRead();
    return;
  }

  detachPastHeaderReadTracking();

  if (!("IntersectionObserver" in window)) {
    return;
  }

  if (readObserver) {
    readObserver.disconnect();
  }

  readObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.98) {
        return;
      }
      const card = entry.target;
      const rawID = card.getAttribute("data-entry-id");
      const entryID = Number(rawID || "0");
      if (entryID > 0) {
        queueEntryAsRead(entryID);
      }
      readObserver.unobserve(card);
    });
  }, {
    threshold: [0.98, 1]
  });

  const cards = entriesContainer.querySelectorAll("article[data-entry-id]");
  cards.forEach(function (card) {
    readObserver.observe(card);
  });
}

export function dedupeVisibleEntryCards() {
  const entriesContainer = document.getElementById("entries-content");
  if (!entriesContainer) {
    return;
  }

  const seen = new Set();
  const cards = entriesContainer.querySelectorAll("article[data-entry-id]");
  cards.forEach(function (card) {
    const rawID = String(card.getAttribute("data-entry-id") || "").trim();
    if (!rawID) {
      return;
    }
    if (seen.has(rawID)) {
      if (card.parentElement) {
        card.parentElement.removeChild(card);
      }
      return;
    }
    seen.add(rawID);
  });
}

export function syncDashboardLoadedEntryCount() {
  // Counter is sourced from backend as global unread articles total.
}

function unreadNewItemsBannerID() {
  return "entries-new-items-banner";
}

function unreadNewItemsButtonID() {
  return "entries-new-items-button";
}

function updateUnreadNewItemsBanner() {
  const banner = document.getElementById(unreadNewItemsBannerID());
  const button = document.getElementById(unreadNewItemsButtonID());
  if (!(banner instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) {
    return;
  }

  const count = pendingUnreadRefreshCardsByID.size;
  if (count <= 0) {
    banner.remove();
    return;
  }

  button.textContent = "Show " + String(count) + " new article" + (count === 1 ? "" : "s");
}

function applyPendingUnreadRefreshEntries() {
  const entriesGrid = document.getElementById("entries-grid");
  if (!(entriesGrid instanceof HTMLElement)) {
    pendingUnreadRefreshCardsByID.clear();
    updateUnreadNewItemsBanner();
    return;
  }

  if (pendingUnreadRefreshCardsByID.size === 0) {
    updateUnreadNewItemsBanner();
    return;
  }

  const cardsToInsert = Array.from(pendingUnreadRefreshCardsByID.values());
  pendingUnreadRefreshCardsByID.clear();
  updateUnreadNewItemsBanner();

  for (let i = cardsToInsert.length - 1; i >= 0; i -= 1) {
    const template = document.createElement("template");
    template.innerHTML = String(cardsToInsert[i] || "").trim();
    const card = template.content.firstElementChild;
    if (card) {
      entriesGrid.insertBefore(card, entriesGrid.firstChild);
    }
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
  dedupeVisibleEntryCards();
  initEntryReadObserver();
}

function ensureUnreadNewItemsBanner(entriesContainer) {
  if (!(entriesContainer instanceof HTMLElement)) {
    return;
  }

  let banner = document.getElementById(unreadNewItemsBannerID());
  if (!(banner instanceof HTMLElement)) {
    banner = document.createElement("div");
    banner.id = unreadNewItemsBannerID();
    banner.className = "entries-new-items-banner";

    const button = document.createElement("button");
    button.id = unreadNewItemsButtonID();
    button.type = "button";
    button.className = "entries-new-items-button";
    button.addEventListener("click", function () {
      applyPendingUnreadRefreshEntries();
    });

    banner.appendChild(button);
    entriesContainer.insertBefore(banner, entriesContainer.firstChild);
  }

  updateUnreadNewItemsBanner();
}

export function resetPendingUnreadRefreshQueue() {
  pendingUnreadRefreshCardsByID.clear();
  const banner = document.getElementById(unreadNewItemsBannerID());
  if (banner instanceof HTMLElement) {
    banner.remove();
  }
}

export function mergeAutoRefreshedUnreadEntries(responseText) {
  const entriesContainer = document.getElementById("entries-content");
  const entriesGrid = document.getElementById("entries-grid");
  if (!(entriesContainer instanceof HTMLElement) || !(entriesGrid instanceof HTMLElement)) {
    return;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(responseText || ""), "text/html");
  const incomingRoot = doc.querySelector("#entries-content") || doc.body;
  if (!incomingRoot) {
    return;
  }

  const existingIDs = new Set();
  entriesGrid.querySelectorAll("article[data-entry-id]").forEach(function (card) {
    const id = String(card.getAttribute("data-entry-id") || "").trim();
    if (id) {
      existingIDs.add(id);
    }
  });
  pendingUnreadRefreshCardsByID.forEach(function (_html, id) {
    existingIDs.add(id);
  });

  const incomingCards = incomingRoot.querySelectorAll("article[data-entry-id]");
  if (incomingCards.length === 0) {
    return;
  }

  // Only prepend a leading block of truly new items before the first overlap.
  // This keeps unread view stable and avoids "backfilling" older unseen cards
  // into the top when server-side unread pages shift after local read actions.
  const cardsToQueue = [];
  let foundOverlap = false;
  for (let i = 0; i < incomingCards.length; i += 1) {
    const card = incomingCards[i];
    const id = String(card.getAttribute("data-entry-id") || "").trim();
    if (!id) {
      continue;
    }

    if (existingIDs.has(id)) {
      foundOverlap = true;
      break;
    }

    cardsToQueue.push({ id: id, html: card.outerHTML });
  }

  if (!foundOverlap) {
    return;
  }

  if (cardsToQueue.length === 0) {
    return;
  }

  cardsToQueue.forEach(function (item) {
    if (!pendingUnreadRefreshCardsByID.has(item.id)) {
      pendingUnreadRefreshCardsByID.set(item.id, item.html);
    }
  });

  ensureUnreadNewItemsBanner(entriesContainer);
}
